/**
 * Email Service - STORY-025 & STORY-026
 *
 * Robust email service with:
 * - Strict recipient validation (NEVER hardcode emails)
 * - Queue system with retry mechanism
 * - HTML template support
 * - Multiple provider support (Resend / Nodemailer SMTP)
 */

import { supabase } from '@/lib/supabase'
import type {
  EmailTemplate,
  EmailLog,
  EmailQueueItem,
  EmailSettings,
  SendEmailRequest,
  SendEmailResponse,
  EmailStats,
  EmailLogFilters,
  EmailStatus,
  EmailTemplateFormData,
  EmailSettingsFormData,
} from '@/types/email'
import { emailRecipientSchema, sendEmailSchema } from '@/types/email'

// Constants
const DEFAULT_MAX_RETRIES = 3

/**
 * Get all email recipients for a project (members with receive_emails = true)
 * Returns emails excluding the main client email to avoid duplicates
 */
async function getProjectEmailRecipients(projectId: string, excludeEmail?: string): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('project_members')
      .select('user_email')
      .eq('project_id', projectId)
      .eq('receive_emails', true)

    if (error || !data) return []

    return data
      .map(m => m.user_email)
      .filter(email => email && email !== excludeEmail)
  } catch {
    console.error('Erreur récupération destinataires projet:', projectId)
    return []
  }
}
const DEFAULT_RETRY_DELAY_MINUTES = 5
const EMAIL_QUEUE_TABLE = 'email_queue'
const EMAIL_LOGS_TABLE = 'email_logs'
const EMAIL_TEMPLATES_TABLE = 'email_templates'
const EMAIL_SETTINGS_TABLE = 'email_settings'

/**
 * Validate email recipient strictly
 * NEVER hardcode emails - always validate dynamically
 */
function validateRecipient(email: string): { valid: boolean; error?: string } {
  try {
    emailRecipientSchema.parse(email)

    // Additional validation checks
    const emailLower = email.toLowerCase().trim()

    // Check for common invalid patterns
    if (emailLower.includes('test@') && process.env.NODE_ENV === 'production') {
      return { valid: false, error: 'Test emails not allowed in production' }
    }

    // Check for disposable email domains (basic list)
    const disposableDomains = [
      'mailinator.com',
      'guerrillamail.com',
      'tempmail.com',
      '10minutemail.com',
      'throwaway.email',
    ]
    const domain = emailLower.split('@')[1]
    if (disposableDomains.includes(domain)) {
      return { valid: false, error: 'Disposable email addresses not allowed' }
    }

    return { valid: true }
  } catch {
    return { valid: false, error: 'Invalid email format' }
  }
}

/**
 * Replace template variables with actual values
 */
function replaceTemplateVariables(
  content: string,
  variables: Record<string, string>
): string {
  let result = content
  for (const [key, value] of Object.entries(variables)) {
    // Support both {{var}} and {{ var }} formats
    const regex = new RegExp(`\\{\\{\\s*${key.replace(/[{}]/g, '')}\\s*\\}\\}`, 'g')
    result = result.replace(regex, value)
  }
  return result
}

/**
 * Check if current time is within allowed sending hours
 */
function isWithinSendingHours(settings: EmailSettings): boolean {
  if (!settings.sending_enabled) return false

  const now = new Date()
  const timezone = settings.timezone || 'Europe/Paris'

  // Get current time in the configured timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  })

  const parts = formatter.formatToParts(now)
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0')
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0')
  const weekdayStr = parts.find((p) => p.type === 'weekday')?.value || 'Mon'

  // Convert weekday string to number (0=Sunday, 1=Monday, etc.)
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  }
  const currentDay = weekdayMap[weekdayStr] ?? 1

  // Check if current day is allowed
  if (!settings.allowed_sending_days.includes(currentDay)) {
    return false
  }

  // Parse start and end times
  const [startHour, startMinute] = settings.allowed_sending_start.split(':').map(Number)
  const [endHour, endMinute] = settings.allowed_sending_end.split(':').map(Number)

  const currentMinutes = hour * 60 + minute
  const startMinutes = startHour * 60 + startMinute
  const endMinutes = endHour * 60 + endMinute

  return currentMinutes >= startMinutes && currentMinutes <= endMinutes
}

/**
 * Email Service
 */
export const emailService = {
  // ==================== TEMPLATES ====================

  /**
   * Get all email templates
   */
  async getTemplates(): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from(EMAIL_TEMPLATES_TABLE)
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw new Error(`Failed to fetch templates: ${error.message}`)
    return data || []
  },

  /**
   * Get template by ID
   */
  async getTemplateById(id: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from(EMAIL_TEMPLATES_TABLE)
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch template: ${error.message}`)
    }
    return data
  },

  /**
   * Get template by type
   */
  async getTemplateByType(type: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from(EMAIL_TEMPLATES_TABLE)
      .select('*')
      .eq('type', type)
      .eq('is_active', true)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch template: ${error.message}`)
    }
    return data
  },

  /**
   * Create new template
   */
  async createTemplate(data: EmailTemplateFormData): Promise<EmailTemplate> {
    const { data: user } = await supabase.auth.getUser()

    const { data: template, error } = await supabase
      .from(EMAIL_TEMPLATES_TABLE)
      .insert({
        ...data,
        created_by: user?.user?.id || null,
      })
      .select()
      .single()

    if (error) throw new Error(`Failed to create template: ${error.message}`)
    return template
  },

  /**
   * Update template
   */
  async updateTemplate(id: string, data: Partial<EmailTemplateFormData>): Promise<EmailTemplate> {
    const { data: template, error } = await supabase
      .from(EMAIL_TEMPLATES_TABLE)
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw new Error(`Failed to update template: ${error.message}`)
    return template
  },

  /**
   * Delete template
   */
  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase.from(EMAIL_TEMPLATES_TABLE).delete().eq('id', id)

    if (error) throw new Error(`Failed to delete template: ${error.message}`)
  },

  // ==================== EMAIL SENDING ====================

  /**
   * Send email with validation and queue support
   */
  async sendEmail(request: SendEmailRequest): Promise<SendEmailResponse> {
    try {
      // Validate request
      const validatedRequest = sendEmailSchema.parse(request)

      // Strict recipient validation
      const recipientValidation = validateRecipient(validatedRequest.to)
      if (!recipientValidation.valid) {
        return {
          success: false,
          error: `Invalid recipient: ${recipientValidation.error}`,
        }
      }

      // Check if email API is enabled (can be disabled via env for local dev)
      if (import.meta.env.VITE_EMAIL_ENABLED === 'false') {
        console.warn('Email sending disabled via VITE_EMAIL_ENABLED=false')
        return { success: false, error: 'Email sending is disabled' }
      }

      // Get template if specified
      let htmlContent = validatedRequest.html
      let textContent = validatedRequest.text || ''
      let subject = validatedRequest.subject

      if (validatedRequest.templateId) {
        const template = await this.getTemplateById(validatedRequest.templateId)
        if (template) {
          htmlContent = template.html_content
          textContent = template.text_content || ''
          subject = template.subject
        }
      }

      // Replace variables in content
      if (validatedRequest.variables) {
        htmlContent = replaceTemplateVariables(htmlContent, validatedRequest.variables)
        textContent = replaceTemplateVariables(textContent, validatedRequest.variables)
        subject = replaceTemplateVariables(subject, validatedRequest.variables)
      }

      // For now, always send immediately (queue support can be added later)
      // Send immediately
      const result = await this.sendEmailNow({
        to: validatedRequest.to,
        toName: validatedRequest.toName,
        subject,
        html: htmlContent,
        text: textContent,
        attachments: validatedRequest.attachments,
      })

      // Log the email (optional - don't fail if logging fails)
      try {
        await this.logEmail({
          template_id: validatedRequest.templateId || null,
          recipient_email: validatedRequest.to,
          recipient_name: validatedRequest.toName || null,
          subject,
          html_content: htmlContent,
          text_content: textContent || null,
          status: result.success ? 'sent' : 'failed',
          sent_at: result.success ? new Date().toISOString() : null,
          error_message: result.error || null,
          reference_type: validatedRequest.referenceType || null,
          reference_id: validatedRequest.referenceId || null,
          metadata: validatedRequest.metadata || null,
        })
      } catch (logError) {
        console.warn('Failed to log email (table may not exist):', logError)
        // Don't fail the email send if logging fails
      }

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Get Bearer token from current Supabase session
   */
  async getAccessToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token || null
  },

  /**
   * Send email immediately via Les Sables Web API
   * API: POST https://api.lessablesweb.fr/api/email/send
   */
  async sendEmailNow(params: {
    to: string
    toName?: string
    subject: string
    html: string
    text?: string
    attachments?: { filename: string; content: string; content_type?: string }[]
  }): Promise<SendEmailResponse> {
    const { to, subject, html, text, attachments } = params

    try {
      const token = await this.getAccessToken()
      if (!token) {
        throw new Error('Non authentifié - impossible d\'envoyer l\'email')
      }

      const API_URL = 'https://api.lessablesweb.fr'

      const response = await fetch(`${API_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          to,
          subject,
          html,
          text: text || undefined,
          ...(attachments?.length ? { attachments } : {}),
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMsg = errorData.error?.message || errorData.error || `Erreur API (${response.status})`
        throw new Error(errorMsg)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error?.message || 'Échec de l\'envoi de l\'email')
      }

      return { success: true, messageId: data.data?.id }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Échec de l\'envoi de l\'email'
      console.error('Email sending error:', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Send email using a server-side template
   * API: POST https://api.lessablesweb.fr/api/email/template
   *
   * Available templates: welcome, password_reset, project_status_changed,
   * invoice_created, quote_ready, project_request_submitted,
   * support_request_created, support_request_updated, appointment_request_received
   */
  async sendTemplateEmail(params: {
    to: string
    template: string
    variables: Record<string, string>
  }): Promise<SendEmailResponse> {
    const { to, template, variables } = params

    try {
      const token = await this.getAccessToken()
      if (!token) {
        throw new Error('Non authentifié - impossible d\'envoyer l\'email')
      }

      const API_URL = 'https://api.lessablesweb.fr'

      const response = await fetch(`${API_URL}/api/email/template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ to, template, variables }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMsg = errorData.error?.message || errorData.error || `Erreur API (${response.status})`
        throw new Error(errorMsg)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error?.message || 'Échec de l\'envoi de l\'email template')
      }

      return { success: true, messageId: data.data?.id }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Échec de l\'envoi de l\'email template'
      console.error('Template email sending error:', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Check email delivery status
   * API: GET https://api.lessablesweb.fr/api/email/status/:id
   */
  async getDeliveryStatus(emailId: string): Promise<{ status: string; sentAt?: string } | null> {
    try {
      const token = await this.getAccessToken()
      if (!token) return null

      const API_URL = 'https://api.lessablesweb.fr'

      const response = await fetch(`${API_URL}/api/email/status/${emailId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) return null

      const data = await response.json()
      if (!data.success) return null

      return { status: data.data.status, sentAt: data.data.sentAt }
    } catch {
      return null
    }
  },

  // ==================== QUEUE ====================

  /**
   * Add email to queue
   */
  async addToQueue(request: SendEmailRequest): Promise<EmailQueueItem> {
    const { data: user } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from(EMAIL_QUEUE_TABLE)
      .insert({
        scheduled_at: request.scheduledAt || new Date().toISOString(),
        priority: request.priority || 'normal',
        template_id: request.templateId || null,
        recipient_email: request.to,
        recipient_name: request.toName || null,
        subject: request.subject,
        html_content: request.html,
        text_content: request.text || null,
        variables: request.variables || {},
        reference_type: request.referenceType || null,
        reference_id: request.referenceId || null,
        metadata: request.metadata || null,
        status: 'pending',
        retry_count: 0,
        max_retries: DEFAULT_MAX_RETRIES,
      })
      .select()
      .single()

    if (error) throw new Error(`Failed to add to queue: ${error.message}`)
    return data
  },

  /**
   * Process email queue (called by cron job or edge function)
   */
  async processQueue(): Promise<{ processed: number; failed: number }> {
    // Check if email is enabled
    if (import.meta.env.VITE_EMAIL_ENABLED === 'false') {
      return { processed: 0, failed: 0 }
    }

    // Get pending emails
    const { data: queueItems, error } = await supabase
      .from(EMAIL_QUEUE_TABLE)
      .select('*')
      .eq('status', 'pending')
      .lte('scheduled_at', new Date().toISOString())
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true })
      .limit(50) // Process up to 50 emails per run

    if (error || !queueItems) {
      console.error('Failed to fetch queue:', error)
      return { processed: 0, failed: 0 }
    }

    let processed = 0
    let failed = 0

    for (const item of queueItems) {
      // Mark as processing
      await supabase
        .from(EMAIL_QUEUE_TABLE)
        .update({ status: 'processing' })
        .eq('id', item.id)

      const result = await this.sendEmailNow({
        to: item.recipient_email,
        toName: item.recipient_name || undefined,
        subject: item.subject,
        html: item.html_content,
        text: item.text_content || undefined,
      })

      if (result.success) {
        // Mark as completed
        await supabase
          .from(EMAIL_QUEUE_TABLE)
          .update({ status: 'completed' })
          .eq('id', item.id)

        // Log success
        await this.logEmail({
          template_id: item.template_id,
          recipient_email: item.recipient_email,
          recipient_name: item.recipient_name,
          subject: item.subject,
          html_content: item.html_content,
          text_content: item.text_content,
          status: 'sent',
          sent_at: new Date().toISOString(),
          reference_type: item.reference_type,
          reference_id: item.reference_id,
          metadata: item.metadata,
        })

        processed++
      } else {
        // Handle failure with retry
        const newRetryCount = item.retry_count + 1

        if (newRetryCount >= item.max_retries) {
          // Max retries reached - mark as failed
          await supabase
            .from(EMAIL_QUEUE_TABLE)
            .update({
              status: 'failed',
              error_message: result.error,
              retry_count: newRetryCount,
            })
            .eq('id', item.id)

          // Log failure
          await this.logEmail({
            template_id: item.template_id,
            recipient_email: item.recipient_email,
            recipient_name: item.recipient_name,
            subject: item.subject,
            html_content: item.html_content,
            text_content: item.text_content,
            status: 'failed',
            failed_at: new Date().toISOString(),
            error_message: result.error || null,
            retry_count: newRetryCount,
            max_retries: item.max_retries,
            reference_type: item.reference_type,
            reference_id: item.reference_id,
            metadata: item.metadata,
          })

          failed++
        } else {
          // Schedule retry
          const retryDelay = DEFAULT_RETRY_DELAY_MINUTES
          const retryAt = new Date(Date.now() + retryDelay * 60 * 1000)

          await supabase
            .from(EMAIL_QUEUE_TABLE)
            .update({
              status: 'pending',
              scheduled_at: retryAt.toISOString(),
              retry_count: newRetryCount,
              error_message: result.error,
            })
            .eq('id', item.id)
        }
      }
    }

    return { processed, failed }
  },

  /**
   * Get queue items
   */
  async getQueueItems(status?: string): Promise<EmailQueueItem[]> {
    let query = supabase
      .from(EMAIL_QUEUE_TABLE)
      .select('*')
      .order('scheduled_at', { ascending: true })

    if (status) {
      query = query.eq('status', status)
    }

    const { data, error } = await query

    if (error) throw new Error(`Failed to fetch queue: ${error.message}`)
    return data || []
  },

  // ==================== LOGS ====================

  /**
   * Log email
   */
  async logEmail(
    log: Omit<EmailLog, 'id' | 'created_at' | 'updated_at' | 'sent_by'>
  ): Promise<EmailLog> {
    const { data: user } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from(EMAIL_LOGS_TABLE)
      .insert({
        ...log,
        sent_by: user?.user?.id || null,
      })
      .select()
      .single()

    if (error) throw new Error(`Failed to log email: ${error.message}`)
    return data
  },

  /**
   * Get email logs with filters
   */
  async getLogs(
    filters?: EmailLogFilters
  ): Promise<{ logs: EmailLog[]; total: number }> {
    let query = supabase
      .from(EMAIL_LOGS_TABLE)
      .select('*, template:email_templates(*)', { count: 'exact' })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }
    if (filters?.templateId) {
      query = query.eq('template_id', filters.templateId)
    }
    if (filters?.referenceType) {
      query = query.eq('reference_type', filters.referenceType)
    }
    if (filters?.referenceId) {
      query = query.eq('reference_id', filters.referenceId)
    }
    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate)
    }
    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate)
    }
    if (filters?.search) {
      query = query.or(
        `recipient_email.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`
      )
    }

    // Pagination
    const page = filters?.page || 1
    const limit = filters?.limit || 20
    const from = (page - 1) * limit
    const to = from + limit - 1

    query = query.order('created_at', { ascending: false }).range(from, to)

    const { data, error, count } = await query

    if (error) throw new Error(`Failed to fetch logs: ${error.message}`)
    return { logs: data || [], total: count || 0 }
  },

  /**
   * Get email log by ID
   */
  async getLogById(id: string): Promise<EmailLog | null> {
    const { data, error } = await supabase
      .from(EMAIL_LOGS_TABLE)
      .select('*, template:email_templates(*)')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch log: ${error.message}`)
    }
    return data
  },

  /**
   * Update email log status (for webhooks)
   */
  async updateLogStatus(
    id: string,
    status: EmailStatus,
    timestamp?: string
  ): Promise<EmailLog> {
    const updates: Partial<EmailLog> = {
      status,
      updated_at: new Date().toISOString(),
    }

    const timestampField = `${status}_at` as keyof EmailLog
    if (timestamp && timestampField in updates) {
      (updates as Record<string, unknown>)[timestampField] = timestamp
    }

    const { data, error } = await supabase
      .from(EMAIL_LOGS_TABLE)
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw new Error(`Failed to update log: ${error.message}`)
    return data
  },

  /**
   * Get email stats
   */
  async getStats(startDate?: string, endDate?: string): Promise<EmailStats> {
    let query = supabase.from(EMAIL_LOGS_TABLE).select('status')

    if (startDate) query = query.gte('created_at', startDate)
    if (endDate) query = query.lte('created_at', endDate)

    const { data, error } = await query

    if (error) throw new Error(`Failed to fetch stats: ${error.message}`)

    const logs = data || []
    const total = logs.length

    const counts = {
      sent: logs.filter((l) => l.status === 'sent').length,
      delivered: logs.filter((l) => l.status === 'delivered').length,
      opened: logs.filter((l) => l.status === 'opened').length,
      clicked: logs.filter((l) => l.status === 'clicked').length,
      bounced: logs.filter((l) => l.status === 'bounced').length,
      failed: logs.filter((l) => l.status === 'failed').length,
    }

    const totalSentOrDelivered = counts.sent + counts.delivered + counts.opened + counts.clicked

    return {
      total_sent: counts.sent,
      total_delivered: counts.delivered,
      total_opened: counts.opened,
      total_clicked: counts.clicked,
      total_bounced: counts.bounced,
      total_failed: counts.failed,
      delivery_rate: totalSentOrDelivered > 0 ? (counts.delivered / totalSentOrDelivered) * 100 : 0,
      open_rate: counts.delivered > 0 ? (counts.opened / counts.delivered) * 100 : 0,
      click_rate: counts.opened > 0 ? (counts.clicked / counts.opened) * 100 : 0,
      bounce_rate: total > 0 ? (counts.bounced / total) * 100 : 0,
    }
  },

  // ==================== SETTINGS ====================

  /**
   * Get email settings
   */
  async getSettings(): Promise<EmailSettings | null> {
    const { data, error } = await supabase
      .from(EMAIL_SETTINGS_TABLE)
      .select('*')
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch settings: ${error.message}`)
    }
    return data
  },

  /**
   * Update email settings
   */
  async updateSettings(settings: Partial<EmailSettingsFormData>): Promise<EmailSettings> {
    // Check if settings exist
    const existing = await this.getSettings()

    if (existing) {
      const { data, error } = await supabase
        .from(EMAIL_SETTINGS_TABLE)
        .update({
          ...settings,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw new Error(`Failed to update settings: ${error.message}`)
      return data
    } else {
      const { data, error } = await supabase
        .from(EMAIL_SETTINGS_TABLE)
        .insert(settings)
        .select()
        .single()

      if (error) throw new Error(`Failed to create settings: ${error.message}`)
      return data
    }
  },

  /**
   * Send email to all project members with receive_emails = true
   * Sends to the main recipient AND all additional project members
   */
  async sendEmailToProject(
    projectId: string,
    request: SendEmailRequest
  ): Promise<{ mainResult: SendEmailResponse; memberResults: { email: string; result: SendEmailResponse }[] }> {
    // Send to the main recipient first
    const mainResult = await this.sendEmail(request)

    // Get additional project members (excluding main recipient)
    const additionalRecipients = await getProjectEmailRecipients(projectId, request.to)

    const memberResults: { email: string; result: SendEmailResponse }[] = []

    for (const memberEmail of additionalRecipients) {
      try {
        const result = await this.sendEmail({
          ...request,
          to: memberEmail,
          toName: undefined, // On n'a pas le nom des membres additionnels
        })
        memberResults.push({ email: memberEmail, result })
      } catch (err) {
        console.error(`Erreur envoi email membre ${memberEmail}:`, err)
        memberResults.push({
          email: memberEmail,
          result: { success: false, error: err instanceof Error ? err.message : 'Erreur inconnue' },
        })
      }
    }

    return { mainResult, memberResults }
  },

  /**
   * Test email settings by sending a test email
   */
  async testSettings(testEmail: string): Promise<SendEmailResponse> {
    const validation = validateRecipient(testEmail)
    if (!validation.valid) {
      return { success: false, error: validation.error }
    }

    return await this.sendEmailNow({
      to: testEmail,
      subject: 'Test Email - CRM Admin',
      html: `
        <h1>Test Email</h1>
        <p>This is a test email from CRM Admin.</p>
        <p>If you receive this email, your email configuration is working correctly.</p>
        <p>Sent at: ${new Date().toLocaleString('fr-FR')}</p>
      `,
      text: 'This is a test email from CRM Admin. If you receive this email, your email configuration is working correctly.',
    })
  },
}

export default emailService
